
import { useAbielAudio } from '@/components/audio/AbielAudioContext';
import { LAYERS } from '@/utils/layers';

export interface ControlVisibilityState {
  shouldShowPrayerButton: boolean;
  shouldShowFloatingActions: boolean;
  containerClasses: string;
  transitionClasses: string;
}

export interface ControlVisibilityConfig {
  positioning: 'fixed' | 'relative' | 'absolute-above-audio';
  hasCTAButton: boolean;
  prayerId: string;
  currentSessionId?: string;
  isPlaying?: boolean;
  isExpanded?: boolean;
  scrollY?: number;
}

export function useControlVisibility(config: ControlVisibilityConfig): ControlVisibilityState {
  const { currentSessionId, isPlaying } = useAbielAudio();
  
  const {
    positioning,
    hasCTAButton,
    prayerId,
    scrollY = 0,
    isExpanded = false
  } = config;

  console.debug('[useControlVisibility] State:', {
    positioning,
    hasCTAButton,
    prayerId,
    currentSessionId,
    isPlaying,
    isExpanded
  });

  // Core visibility logic - show prayer button when positioning is fixed OR absolute-above-audio and no CTA button exists
  const shouldShowPrayerButton = (positioning === 'fixed' || positioning === 'absolute-above-audio') && !hasCTAButton;
  
  // Show floating actions based on scroll position and context - ALWAYS SHOW for fixed positioning and absolute-above-audio
  const shouldShowFloatingActions = positioning === 'fixed' || positioning === 'absolute-above-audio' || scrollY < 100;

  // Check if audio is playing for this prayer
  const isAudioPlaying = isPlaying && currentSessionId === `prayer_${prayerId}`;

  // Dynamic positioning classes with smooth transitions - STABLE POSITIONING
  let containerClasses = '';
  const transitionClasses = 'transition-all duration-300 ease-in-out';
  
  if (positioning === 'fixed') {
    // FLOATING ACTION BAR DESIGN PATTERN:
    // We use 'flex-col-reverse' to create an upward-growing action bar from a fixed bottom position.
    // This ensures new buttons are added at the visual TOP without pushing existing content down.
    // 
    // Visual Order (top to bottom): [Newest] → [Oldest] 
    // JSX Order (bottom to top):    [Newest] → [Oldest]
    // 
    // Benefits:
    // - Maintains stable bottom anchor point for user interaction
    // - New features don't disrupt existing button positions
    // - Prevents content overlap when action bar grows
    // - Provides consistent UX as buttons are added/removed
    //
    // STABLE POSITIONING: No dynamic spacing that causes movement
    containerClasses = `fixed bottom-40 right-4 flex flex-col-reverse items-center gap-3 z-[${LAYERS.ACTION_CNT}] ${transitionClasses}`;
  } else if (positioning === 'absolute-above-audio') {
    // Fixed positioning relative to CTA button top edge
    // CTA button is ~64px from bottom, add 56px for action bar spacing above author overlay
    const ctaRelativeBottom = 'bottom-[120px]';
    containerClasses = `fixed ${ctaRelativeBottom} right-4 flex flex-col-reverse items-center gap-3 z-[${LAYERS.ACTION_CNT}] ${transitionClasses}`;
  } else {
    // Relative positioning still uses flex-col-reverse for visual consistency
    // STABLE SPACING: Consistent gap regardless of audio state
    containerClasses = `flex flex-col-reverse items-center gap-3 ${transitionClasses}`;
  }

  console.debug('[useControlVisibility] Result:', {
    shouldShowPrayerButton,
    shouldShowFloatingActions,
    isAudioPlaying,
    containerClasses
  });

  return {
    shouldShowPrayerButton,
    shouldShowFloatingActions,
    containerClasses,
    transitionClasses
  };
}
